/**
 * Priority Proposal Store
 *
 * Manages PriorityProposals — the governance artifacts created when a
 * facilitator "lifts" session results into the ball lane.
 *
 * Zustand safety rules applied:
 * - All selectors return primitive values or stable references
 * - Methods that return computed values are NOT called inside selectors
 * - Consumers filter raw `proposals` array in useMemo, never in selectors
 */

import { create } from 'zustand';
import { generateUUID, getSafeSupabaseClient, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { sendNotification } from '../../lib/notificationService';
import type {
  PriorityProposal,
  PriorityProposalStatus,
  ProposalCatchballMessage,
  CreatePriorityProposalInput,
} from '../../types/prioritization/priorityProposal';

// ─── State interface ──────────────────────────────────────────────────────────

interface PriorityProposalState {
  /** All proposals in memory */
  proposals: PriorityProposal[];

  /** Load proposals for a cycle (or all workspace proposals if cycleId omitted) from Supabase */
  fetchForCycle: (cycleId?: string) => Promise<void>;

  /**
   * Load all proposals attached to a specific Catchball loop.
   * Used by the workbench to show the proposal(s) the loop has produced.
   */
  fetchForLoop: (loopId: string) => Promise<void>;

  /**
   * Ratify a proposal AND call approve_priority_list to stamp
   * leadership_approved=true on every ranked item.
   * Returns { success, approvedCount, error? }
   */
  ratifyAndPublish: (
    proposalId: string,
    actorId: string,
  ) => Promise<{ success: boolean; approvedCount?: number; error?: string }>;

  // ── Mutations ────────────────────────────────────────────────────────────

  /**
   * Lift: convert session results into a governance proposal via the
   * lift_priority_proposal RPC (§2.3a compliant — transactional, audited).
   * Returns the canonical proposal ID on success, or throws on failure.
   */
  createProposal: (input: CreatePriorityProposalInput) => Promise<string>;

  /**
   * Advance a proposal to the next status in the governance pipeline via the
   * advance_proposal_status RPC (§2.3a compliant — transactional, audited).
   * Re-fetches canonical state from DB after the RPC.
   */
  updateStatus: (id: string, status: PriorityProposalStatus, actorId?: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Update editable fields on a draft proposal (title, rationale, openQuestions).
   * Only allowed while status === 'draft'.
   */
  updateProposal: (id: string, updates: Partial<Pick<PriorityProposal, 'title' | 'rationale' | 'openQuestions' | 'scheduledPublishDate' | 'tosserId'>>) => void;

  /**
   * Add a catchball message from either the holder or tosser.
   */
  addMessage: (
    proposalId: string,
    message: Omit<ProposalCatchballMessage, 'id' | 'proposalId' | 'createdAt'>,
  ) => void;

  /**
   * Expressly share a proposal with an additional user outside the
   * default holder/tosser visibility pair.
   */
  shareWith: (proposalId: string, userId: string) => void;

  /**
   * Delete a draft proposal (only drafts may be deleted).
   */
  deleteDraft: (id: string) => void;
}

// ─── Row mapper ───────────────────────────────────────────────────────────────

function rowToProposal(row: Record<string, unknown>): PriorityProposal {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    loopId: (row.loop_id as string | null) ?? undefined,
    trackIds: (row.track_ids as string[]) ?? [],
    sessionIds: (row.session_ids as string[]) ?? [],
    title: row.title as string,
    rationale: (row.rationale as string) ?? '',
    openQuestions: (row.open_questions as string | null) ?? undefined,
    rankedItems: (row.ranked_items as PriorityProposal['rankedItems']) ?? [],
    status: (row.status as PriorityProposalStatus) ?? 'catchball',
    createdBy: row.created_by as string,
    liftedAt: new Date(row.lifted_at as string),
    ratifiedAt: row.ratified_at ? new Date(row.ratified_at as string) : undefined,
    publishedAt: row.published_at ? new Date(row.published_at as string) : undefined,
    holderId: (row.holder_id as string | null) ?? undefined,
    tosserId: (row.tosser_id as string | null) ?? undefined,
    catchballMessages: [],
    visibleTo: [],
  };
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const usePriorityProposalStore = create<PriorityProposalState>((set, get) => ({
  proposals: [],

  // ── fetchForCycle ───────────────────────────────────────────────────────

  fetchForCycle: async (cycleId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let query = supabase
      .from('priority_proposals')
      .select('*')
      .order('lifted_at', { ascending: false });
    if (cycleId) query = query.eq('cycle_id', cycleId);
    const { data, error } = await query;
    if (error || !data) return;
    const loaded: PriorityProposal[] = data.map(rowToProposal);
    set((s) => {
      // Merge: keep locally-created proposals not yet in DB, replace those that are
      const loadedIds = new Set(loaded.map((p) => p.id));
      const localOnly = s.proposals.filter((p) => !loadedIds.has(p.id));
      return { proposals: [...loaded, ...localOnly] };
    });
  },

  fetchForLoop: async (loopId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const { data, error } = await supabase
      .from('priority_proposals')
      .select('*')
      .eq('loop_id', loopId)
      .order('lifted_at', { ascending: false });
    if (error || !data) return;
    const loaded: PriorityProposal[] = data.map(rowToProposal);
    set((s) => {
      const merged = s.proposals.map((p) => {
        const updated = loaded.find((l) => l.id === p.id);
        return updated ?? p;
      });
      const newOnes = loaded.filter((p) => !s.proposals.find((e) => e.id === p.id));
      return { proposals: [...merged, ...newOnes] };
    });
  },

  // ── createProposal ──────────────────────────────────────────────────────
  // §2.3a: uses lift_priority_proposal RPC — transactional, audited.
  // No optimistic update; canonical state re-fetched from DB after RPC.

  createProposal: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) throw new Error('Database not available');

    const workspaceId = getCurrentWorkspaceId();

    const { data, error } = await supabase.rpc('lift_priority_proposal', {
      p_actor_id:       input.createdBy || null,
      p_cycle_id:       input.cycleId,
      p_loop_id:        input.loopId || null,
      p_title:          input.title,
      p_rationale:      input.rationale,
      p_session_ids:    input.sessionIds,
      p_track_ids:      input.trackIds,
      p_ranked_items:   input.rankedItems,
      p_open_questions: input.openQuestions || null,
      p_holder_id:      input.holderId || null,
      p_tosser_id:      input.tosserId || null,
      p_workspace_id:   workspaceId || null,
    });

    if (error) {
      console.error('[priorityProposalStore] lift_priority_proposal RPC error:', error.message);
      throw new Error(error.message);
    }

    const result = data as { success: boolean; proposal?: Record<string, unknown>; error?: string };
    if (!result.success) {
      throw new Error(result.error ?? 'Lift failed');
    }

    // Map canonical DB row → typed proposal
    const proposal: PriorityProposal = rowToProposal(result.proposal!);

    set((s) => {
      const existing = s.proposals.find((p) => p.id === proposal.id);
      if (existing) {
        return { proposals: s.proposals.map((p) => p.id === proposal.id ? proposal : p) };
      }
      return { proposals: [...s.proposals, proposal] };
    });

    // Notify the tosser (the person who tossed the ball to this level) that
    // a proposal has been lifted and is awaiting their review in Catchball.
    if (proposal.tosserId && proposal.tosserId !== proposal.createdBy) {
      void sendNotification({
        event: 'priority_proposal_lifted',
        channels: ['in_app'],
        recipientIds: [proposal.tosserId],
        senderId: proposal.createdBy,
        title: 'A proposal has been submitted for your review',
        body: `"${proposal.title}" is ready for Catchball review.`,
        actionUrl: proposal.loopId
          ? `/prioritization/cascade/workbench/${proposal.cycleId}/${proposal.loopId}`
          : `/prioritization/governance/proposals`,
      });
    }

    return proposal.id;
  },

  // ── updateStatus ────────────────────────────────────────────────────────
  // §2.3a: uses advance_proposal_status RPC — transactional, audited.
  // No optimistic update; canonical state re-fetched from DB after RPC.

  updateStatus: async (id, status, actorId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Database not available' };

    const actor = actorId ?? get().proposals.find((p) => p.id === id)?.createdBy ?? null;

    const { data, error } = await supabase.rpc('advance_proposal_status', {
      p_actor_id:    actor,
      p_proposal_id: id,
      p_new_status:  status,
    });

    if (error) {
      console.error('[priorityProposalStore] advance_proposal_status RPC error:', error.message);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; proposal?: Record<string, unknown>; error?: string };
    if (!result.success) {
      return { success: false, error: result.error ?? 'Status advance failed' };
    }

    // Update local state from canonical DB row
    const row = result.proposal!;
    set((s) => ({
      proposals: s.proposals.map((p) => {
        if (p.id !== id) return p;
        return {
          ...p,
          status: (row.status as PriorityProposalStatus) ?? status,
          ratifiedAt: row.ratified_at ? new Date(row.ratified_at as string) : p.ratifiedAt,
          publishedAt: row.published_at ? new Date(row.published_at as string) : p.publishedAt,
        };
      }),
    }));

    return { success: true };
  },

  // ── updateProposal ──────────────────────────────────────────────────────

  updateProposal: (id, updates) => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p.id === id ? { ...p, ...updates } : p,
      ),
    }));
  },

  // ── addMessage ──────────────────────────────────────────────────────────

  addMessage: (proposalId, message) => {
    const msg: ProposalCatchballMessage = {
      id: generateUUID(),
      proposalId,
      createdAt: new Date(),
      ...message,
    };

    set((s) => ({
      proposals: s.proposals.map((p) =>
        p.id === proposalId
          ? { ...p, catchballMessages: [...p.catchballMessages, msg] }
          : p,
      ),
    }));
  },

  // ── shareWith ───────────────────────────────────────────────────────────

  shareWith: (proposalId, userId) => {
    set((s) => ({
      proposals: s.proposals.map((p) => {
        if (p.id !== proposalId) return p;
        if (p.visibleTo.includes(userId)) return p;
        return { ...p, visibleTo: [...p.visibleTo, userId] };
      }),
    }));
  },

  // ── deleteDraft ─────────────────────────────────────────────────────────

  deleteDraft: (id) => {
    set((s) => ({
      proposals: s.proposals.filter(
        (p) => !(p.id === id && p.status === 'draft'),
      ),
    }));
  },

  // ── ratifyAndPublish ─────────────────────────────────────────────────────

  ratifyAndPublish: async (proposalId, actorId) => {
    const proposal = get().proposals.find((p) => p.id === proposalId);
    if (!proposal) return { success: false, error: 'Proposal not found' };

    const itemIds = proposal.rankedItems.map((i) => i.itemId).filter(Boolean);
    if (itemIds.length === 0) {
      return { success: false, error: 'No items in this proposal to approve' };
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Database not available' };

    // 1) Advance to 'ratified' — but if already ratified (e.g. a previous attempt
    //    succeeded on the status step but failed on the approval step), skip the
    //    advance and go straight to approve_priority_list.
    if (proposal.status !== 'ratified') {
      const statusResult = await get().updateStatus(proposalId, 'ratified', actorId);
      if (!statusResult.success) {
        return { success: false, error: statusResult.error };
      }
    }

    // 2) Call approve_priority_list RPC to stamp leadership_approved on items
    const { data, error } = await supabase.rpc('approve_priority_list', {
      p_actor_id: actorId,
      p_item_ids: itemIds,
      p_cycle_id: proposal.cycleId ?? null,
      p_loop_id: null,
      p_note: `Ratified via governance pipeline — proposal: ${proposal.title}`,
    });

    if (error) {
      console.error('[priorityProposalStore] approve_priority_list failed:', error.message);
      return { success: false, error: error.message };
    }

    const result = data as { success?: boolean; approvedCount?: number; error?: string };
    if (!result?.success) {
      return { success: false, error: result?.error ?? 'Approval RPC returned failure' };
    }

    return { success: true, approvedCount: result.approvedCount };
  },
}));
